import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
    Alert, Avatar, Button, Card, Drawer, Empty, Form, Input, List, Popconfirm, Segmented, Space, Tag, Tooltip, Typography, message,
} from 'antd';
import { PlusOutlined, SafetyOutlined, EditOutlined, DeleteOutlined, CrownOutlined } from '@ant-design/icons';
import { usersApi } from '../../../api/client';
import {
    ACCESS_LEVELS, AccessLevel, MANAGED_RESOURCES, PERMISSION_MODULE_GROUPS, levelFromActions,
} from '../../../config/permissionModules';

const { Text, Title } = Typography;

interface Permission {
    id: string;
    resource: string;
    action: string;
}

interface Role {
    id: string;
    name: string;
    description?: string;
    isSystem: boolean;
    systemRole?: string | null;
    priority?: number;
    color?: string;
    permissions?: Permission[];
}

type Levels = Record<string, AccessLevel>;

const LEVEL_COLORS: Record<AccessLevel, string> = { none: 'default', read: 'blue', edit: 'gold', full: 'green' };

function levelsFromPermissions(perms: Permission[] = []): Levels {
    const actionsByResource: Record<string, string[]> = {};
    perms.forEach((p) => {
        (actionsByResource[p.resource] ||= []).push(p.action);
    });
    const levels: Levels = {};
    MANAGED_RESOURCES.forEach((r) => {
        levels[r] = levelFromActions(actionsByResource[r] || []);
    });
    return levels;
}

function RoleSummary({ role }: { role: Role }) {
    if (role.systemRole === 'OWNER') {
        return <Tag color="purple">Pełny dostęp do wszystkiego</Tag>;
    }
    const levels = levelsFromPermissions(role.permissions);
    const granted = PERMISSION_MODULE_GROUPS.flatMap((g) => g.modules).filter((m) => levels[m.resource] !== 'none');
    if (granted.length === 0) {
        return <Text type="secondary">Brak dostępu do żadnej zakładki</Text>;
    }
    return (
        <Space size={[4, 4]} wrap>
            {granted.map((m) => {
                const level = ACCESS_LEVELS.find((l) => l.value === levels[m.resource])!;
                return (
                    <Tag key={m.resource} color={LEVEL_COLORS[level.value]}>
                        {m.label}: {level.label}
                    </Tag>
                );
            })}
        </Space>
    );
}

export function RolesTab() {
    const queryClient = useQueryClient();
    const [isEditorOpen, setIsEditorOpen] = useState(false);
    const [editingRole, setEditingRole] = useState<Role | null>(null);
    const [levels, setLevels] = useState<Levels>({});
    const [form] = Form.useForm();

    const { data: rolesData, isLoading } = useQuery({
        queryKey: ['roles'],
        queryFn: async () => (await usersApi.getRoles()).data,
    });

    const { data: permissionsData } = useQuery({
        queryKey: ['permissions'],
        queryFn: async () => (await usersApi.getPermissions()).data,
    });

    const roles: Role[] = rolesData || [];
    const permissions: Permission[] = permissionsData || [];

    const permissionId = useMemo(() => {
        const map = new Map<string, string>();
        permissions.forEach((p) => map.set(`${p.resource}:${p.action}`, p.id));
        return map;
    }, [permissions]);

    const closeEditor = () => {
        setIsEditorOpen(false);
        setEditingRole(null);
        form.resetFields();
    };

    const onSaved = (text: string) => {
        message.success(`${text}. Użytkownicy z tą rolą muszą wylogować się i zalogować ponownie.`);
        queryClient.invalidateQueries({ queryKey: ['roles'] });
        closeEditor();
    };

    const onError = (error: any) => {
        message.error(error.response?.data?.message || 'Nie udało się zapisać roli');
    };

    const createMutation = useMutation({
        mutationFn: (data: any) => usersApi.createRole(data),
        onSuccess: () => onSaved('Rola utworzona'),
        onError,
    });

    const updateMutation = useMutation({
        mutationFn: ({ id, data }: { id: string; data: any }) => usersApi.updateRole(id, data),
        onSuccess: () => onSaved('Rola zapisana'),
        onError,
    });

    const deleteMutation = useMutation({
        mutationFn: (id: string) => usersApi.deleteRole(id),
        onSuccess: () => {
            message.success('Rola usunięta');
            queryClient.invalidateQueries({ queryKey: ['roles'] });
        },
        onError: (error: any) => message.error(error.response?.data?.message || 'Nie udało się usunąć roli'),
    });

    const openEditor = (role?: Role) => {
        setEditingRole(role || null);
        form.setFieldsValue({ name: role?.name, description: role?.description });
        setLevels(levelsFromPermissions(role?.permissions));
        setIsEditorOpen(true);
    };

    const setAll = (level: AccessLevel) => {
        const next: Levels = {};
        MANAGED_RESOURCES.forEach((r) => {
            next[r] = level;
        });
        setLevels(next);
    };

    const handleSubmit = async () => {
        const values = await form.validateFields();

        // Keep permissions on resources that this editor does not manage
        const untouched = (editingRole?.permissions || [])
            .filter((p) => !MANAGED_RESOURCES.has(p.resource))
            .map((p) => p.id);

        const managed = Object.entries(levels).flatMap(([resource, level]) => {
            const actions = ACCESS_LEVELS.find((l) => l.value === level)?.actions || [];
            return actions.map((a) => permissionId.get(`${resource}:${a}`)).filter(Boolean) as string[];
        });

        const data = { ...values, permissionIds: [...untouched, ...managed] };
        if (editingRole) {
            updateMutation.mutate({ id: editingRole.id, data });
        } else {
            createMutation.mutate(data);
        }
    };

    return (
        <div>
            <Card
                title={<Space><CrownOutlined /> Role w systemie</Space>}
                extra={<Button type="primary" icon={<PlusOutlined />} onClick={() => openEditor()}>Nowa rola</Button>}
            >
                <Alert
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                    message="Rola określa, które zakładki widzi użytkownik i co może w nich robić. Zakładki bez dostępu są ukryte. Po zmianie roli użytkownik musi się wylogować i zalogować ponownie."
                />
                <List
                    loading={isLoading}
                    dataSource={[...roles].sort((a, b) => (b.priority || 0) - (a.priority || 0))}
                    renderItem={(role) => (
                        <List.Item
                            key={role.id}
                            actions={role.isSystem ? [
                                <Tag key="system" color="blue">Rola systemowa – nieedytowalna</Tag>,
                            ] : [
                                <Tooltip title="Edytuj uprawnienia" key="edit">
                                    <Button type="text" icon={<EditOutlined />} onClick={() => openEditor(role)} />
                                </Tooltip>,
                                <Popconfirm key="delete" title="Usunąć rolę?" onConfirm={() => deleteMutation.mutate(role.id)} okText="Tak" cancelText="Nie">
                                    <Tooltip title="Usuń"><Button type="text" danger icon={<DeleteOutlined />} /></Tooltip>
                                </Popconfirm>,
                            ]}
                        >
                            <List.Item.Meta
                                avatar={<Avatar icon={<SafetyOutlined />} style={{ backgroundColor: role.color || '#1890ff' }} />}
                                title={role.name}
                                description={
                                    <Space direction="vertical" size={4}>
                                        {role.description && <Text type="secondary">{role.description}</Text>}
                                        <RoleSummary role={role} />
                                    </Space>
                                }
                            />
                        </List.Item>
                    )}
                    locale={{ emptyText: <Empty description="Brak ról" /> }}
                />
            </Card>

            <Drawer
                title={editingRole ? `Edytuj rolę: ${editingRole.name}` : 'Nowa rola'}
                open={isEditorOpen}
                onClose={closeEditor}
                width={Math.min(820, window.innerWidth)}
                destroyOnClose
                extra={
                    <Space>
                        <Button onClick={closeEditor}>Anuluj</Button>
                        <Button type="primary" onClick={handleSubmit} loading={createMutation.isPending || updateMutation.isPending}>
                            {editingRole ? 'Zapisz' : 'Utwórz'}
                        </Button>
                    </Space>
                }
            >
                <Form form={form} layout="vertical">
                    <Form.Item name="name" label="Nazwa roli" rules={[{ required: true, message: 'Podaj nazwę roli' }]}>
                        <Input placeholder="np. Obsługa opinii" />
                    </Form.Item>
                    <Form.Item name="description" label="Opis">
                        <Input.TextArea rows={2} placeholder="Dla kogo jest ta rola?" />
                    </Form.Item>
                </Form>

                <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 8 }} wrap>
                    <Text strong>Dostęp do zakładek</Text>
                    <Space size={4}>
                        <Text type="secondary">Ustaw wszystkie:</Text>
                        {ACCESS_LEVELS.map((l) => (
                            <Button key={l.value} size="small" onClick={() => setAll(l.value)}>{l.label}</Button>
                        ))}
                    </Space>
                </Space>
                <Text type="secondary" style={{ display: 'block', marginBottom: 16, fontSize: 12 }}>
                    {ACCESS_LEVELS.map((l) => `${l.label} – ${l.hint.toLowerCase()}`).join(' · ')}
                </Text>

                {PERMISSION_MODULE_GROUPS.map((group) => (
                    <div key={group.title} style={{ marginBottom: 20 }}>
                        <Title level={5} style={{ marginBottom: 8 }}>{group.title}</Title>
                        <Card size="small" styles={{ body: { padding: 0 } }}>
                            {group.modules.map((m, i) => (
                                <div
                                    key={m.resource}
                                    style={{
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        gap: 12,
                                        padding: '10px 12px',
                                        borderTop: i === 0 ? undefined : '1px solid rgba(128,128,128,0.15)',
                                    }}
                                >
                                    <div style={{ flex: '1 1 260px', minWidth: 0 }}>
                                        <Text strong>{m.label}</Text>
                                        <div>
                                            <Text type="secondary" style={{ fontSize: 12 }}>{m.description}</Text>
                                        </div>
                                        <div style={{ marginTop: 2 }}>
                                            <Text type="secondary" style={{ fontSize: 12 }}>Zakładki: {m.tabs.join(', ')}</Text>
                                        </div>
                                    </div>
                                    <Segmented
                                        value={levels[m.resource] || 'none'}
                                        onChange={(v) => setLevels((prev) => ({ ...prev, [m.resource]: v as AccessLevel }))}
                                        options={ACCESS_LEVELS.map((l) => ({ value: l.value, label: <Tooltip title={l.hint}>{l.label}</Tooltip> }))}
                                    />
                                </div>
                            ))}
                        </Card>
                    </div>
                ))}
            </Drawer>
        </div>
    );
}
